// @section 1 Documents route — Atlas doc-gen + e-sign ("Act Back") over hq_docs.*
//
// Master/detail: packet -> document list (status chips + signer roll-up) on the
// left, selected document on the right with the VERBATIM viewer (the same HTML
// that drives the signed PDF, injected into an iframe srcDoc so on-screen ==
// signed), a per-signer status tracker, and the send-to-sign action. The
// TEST-vs-binding gate is visible and binding is hard-disabled in the UI: the
// rollup docs are DRAFT pending CPA/attorney review.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listDocumentPackets, listDocuments, getDocument, getDocumentHtml,
  getDocumentStatus, renderDocument, sendDocumentToSign, fileDocumentToDrive,
  type DocPacket, type DocListItem, type DocDetail,
} from '../api';
import { useIsMobile } from '../lib/useIsMobile';

// @block 1.1 Status -> chip color (matches the playbook tracking table semantics)
const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  draft: { bg: '#e5e7eb', fg: '#374151' },
  in_review: { bg: '#fef3c7', fg: '#92400e' },
  sent: { bg: '#dbeafe', fg: '#1e40af' },
  partially_signed: { bg: '#ede9fe', fg: '#5b21b6' },
  executed: { bg: '#dcfce7', fg: '#166534' },
  filed: { bg: '#ccfbf1', fg: '#115e59' },
  pending: { bg: '#e5e7eb', fg: '#374151' },
  viewed: { bg: '#dbeafe', fg: '#1e40af' },
  signed: { bg: '#dcfce7', fg: '#166534' },
  declined: { bg: '#fee2e2', fg: '#991b1b' },
};

function Chip({ status }: { status: string }) {
  const c = STATUS_COLOR[status] ?? { bg: '#e5e7eb', fg: '#374151' };
  return (
    <span style={{
      background: c.bg, color: c.fg, borderRadius: 6, padding: '2px 8px',
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{status.replace(/_/g, ' ')}</span>
  );
}

export default function Documents() {
  const [packets, setPackets] = useState<DocPacket[] | null>(null);
  const [docs, setDocs] = useState<DocListItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DocDetail | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // @block 1.2 Initial load — packets + documents
  useEffect(() => {
    Promise.all([listDocumentPackets(), listDocuments()])
      .then(([p, d]) => { setPackets(p); setDocs(d); if (d.length) setSelectedId(d[0].documentId); })
      .catch((e) => setErr(String(e)));
  }, []);

  // @block 1.3 Load detail + verbatim HTML when selection changes
  const loadDetail = useCallback(async (id: string) => {
    setErr(null); setHtml(null); setDetail(null);
    try {
      const [d, h] = await Promise.all([getDocument(id), getDocumentHtml(id)]);
      setDetail(d); setHtml(h);
    } catch (e) { setErr(String(e)); }
  }, []);

  useEffect(() => { if (selectedId) void loadDetail(selectedId); }, [selectedId, loadDetail]);

  const refreshLists = useCallback(async () => {
    const d = await listDocuments();
    setDocs(d);
  }, []);

  // @block 1.4 Actions — render, send-to-sign (TEST only), refresh status
  const onRender = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true); setErr(null); setNotice(null);
    try {
      const r = await renderDocument(selectedId);
      setNotice(`Rendered ${(r.byteSize / 1024).toFixed(0)} KB — sha256 ${r.sha256.slice(0, 12)}…`);
      await Promise.all([loadDetail(selectedId), refreshLists()]);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }, [selectedId, loadDetail, refreshLists]);

  const onSendTest = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true); setErr(null); setNotice(null);
    try {
      const r = await sendDocumentToSign(selectedId, { isTest: true });
      setNotice(`Sent for TEST signature — request ${r.requestId.slice(0, 8)}…`);
      await Promise.all([loadDetail(selectedId), refreshLists()]);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }, [selectedId, loadDetail, refreshLists]);

  const onFileToDrive = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true); setErr(null); setNotice(null);
    try {
      const r = await fileDocumentToDrive(selectedId);
      setNotice(`Filed to data room — Drive file ${r.executedDriveId?.slice(0, 10)}…`);
      await Promise.all([loadDetail(selectedId), refreshLists()]);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }, [selectedId, loadDetail, refreshLists]);

  const onRefreshStatus = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true); setErr(null);
    try {
      await getDocumentStatus(selectedId);
      await Promise.all([loadDetail(selectedId), refreshLists()]);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }, [selectedId, loadDetail, refreshLists]);

  const packet = packets?.[0];
  const ordered = useMemo(() => (docs ? [...docs].sort((a, b) => a.sortOrder - b.sortOrder) : []), [docs]);
  // @block 1.5 Phone: the 360px+1fr two-pane stacks into one scrolling column
  // (list, then the selected doc with a fixed-height viewer below it).
  const isMobile = useIsMobile();

  return (
    <div style={isMobile
      ? { display: 'grid', gridTemplateColumns: '1fr', gap: 16, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '14px 14px' }
      : { display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16, height: 'calc(100vh - 96px)' }}>
      {/* @block 1.5 Left — packet + document list */}
      <div style={{ overflowY: isMobile ? 'visible' : 'auto', paddingRight: isMobile ? 0 : 4 }}>
        <div style={{ marginBottom: 12 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Documents</h1>
          {packet && (
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--hq-fg-content)', opacity: 0.7 }}>
              {packet.name} · {packet.documentCount} docs · <Chip status={packet.status} />
            </div>
          )}
        </div>

        {/* Legal gate banner */}
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
          borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 12, lineHeight: 1.4,
        }}>
          <strong>DRAFT — pending CPA/attorney review.</strong> Only TEST (non-binding) sends are
          enabled. Binding signatures are gated until the professional review is confirmed.
        </div>

        {!docs && !err && <div style={{ color: '#6b7280' }}>Loading…</div>}
        <div style={{ display: 'grid', gap: 6 }}>
          {ordered.map((d) => {
            const active = d.documentId === selectedId;
            return (
              <button key={d.documentId} onClick={() => setSelectedId(d.documentId)} style={{
                textAlign: 'left', border: active ? '2px solid var(--hq-accent)' : '1px solid #e5e7eb',
                background: active ? '#ffffff' : '#fafafa', borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--hq-fg-content)' }}>{d.title}</span>
                  <Chip status={d.status} />
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                  {d.signedCount}/{d.signerCount} signed{d.hasPdf ? ' · rendered' : ' · not rendered'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* @block 1.6 Right — selected document. On phones the viewer gets a fixed
          70vh so it stays usable inside the single scrolling column (1fr would
          collapse with no defined parent height). */}
      <div style={{ display: 'grid', gridTemplateRows: isMobile ? 'auto auto 70vh' : 'auto auto 1fr', gap: 12, minHeight: 0 }}>
        {err && <div style={{ color: '#991b1b', background: '#fee2e2', padding: 10, borderRadius: 8 }}>{err}</div>}
        {notice && <div style={{ color: '#166534', background: '#dcfce7', padding: 10, borderRadius: 8 }}>{notice}</div>}

        {detail && (
          <>
            {/* Header + actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{detail.title}</h2>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  <Chip status={detail.status} />
                  {detail.renderedSha256 && <span style={{ marginLeft: 8 }}>sha256 {detail.renderedSha256.slice(0, 12)}…</span>}
                  {detail.latestRequest && (
                    <span style={{ marginLeft: 8 }}>
                      {detail.latestRequest.isTest ? 'TEST' : 'BINDING'} · {detail.latestRequest.status}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={onRender} disabled={busy} style={btn('#374151')}>Re-render</button>
                <button onClick={onRefreshStatus} disabled={busy} style={btn('#1e40af')}>Refresh status</button>
                <button onClick={onSendTest} disabled={busy} style={btn('var(--hq-accent)')}>Send for signature (TEST)</button>
                {(detail.status === 'executed' || detail.status === 'filed') && (
                  <button onClick={onFileToDrive} disabled={busy || detail.status === 'filed'} style={btn('#115e59', detail.status === 'filed')}>
                    {detail.status === 'filed' ? 'Filed to data room' : 'File to data room'}
                  </button>
                )}
                <button disabled title="Gated until CPA/attorney review is confirmed" style={btn('#9ca3af', true)}>
                  Send binding (locked)
                </button>
              </div>
            </div>

            {/* Signer tracker */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    <th style={th}>#</th><th style={th}>Signer</th><th style={th}>Role</th>
                    <th style={th}>Status</th><th style={th}>Signed</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.signers.map((s) => (
                    <tr key={s.signerId} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={td}>{s.signOrder}</td>
                      <td style={td}><div style={{ fontWeight: 600 }}>{s.name}</div><div style={{ color: '#6b7280', fontSize: 11 }}>{s.email}</div></td>
                      <td style={td}>{s.role ?? '—'}</td>
                      <td style={td}><Chip status={s.status} /></td>
                      <td style={td}>{s.signedAt ? new Date(s.signedAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* @block 1.7 Verbatim viewer — on-screen == signed */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', minHeight: 0, background: '#fff' }}>
          {html
            ? <iframe title="document" srcDoc={html} style={{ width: '100%', height: '100%', border: 'none' }} />
            : <div style={{ padding: 16, color: '#6b7280' }}>{detail ? 'Rendering…' : 'Select a document.'}</div>}
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600, fontSize: 12, color: '#6b7280' };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
function btn(color: string, disabled = false): React.CSSProperties {
  return {
    background: disabled ? '#f3f4f6' : color, color: disabled ? '#9ca3af' : '#fff',
    border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
